import {AlertIcon, GearIcon} from '@primer/octicons-react'
import {Button, type ButtonProps, Heading, Tooltip} from '@primer/react'
import {clsx} from 'clsx'

import {LABELS} from './fields/constants/labels'
import styles from './SectionHeader.module.css'

export type StyledHeadingProps = {
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  className?: string
}

export type SectionHeaderProps = {
  /**
   * Section heading id
   */
  id?: string
  title: string
  buttonProps?: ButtonProps
  headingProps?: StyledHeadingProps
  readonly?: boolean
  errorPresent?: boolean
  ref?: React.Ref<HTMLButtonElement>
}

export const SectionHeader = ({
  ref,
  id,
  title,
  buttonProps,
  headingProps: {as: Component = 'h3', className: headingClassName} = {},
  readonly,
  errorPresent,
}: SectionHeaderProps) => {
  const content = (
    <div className={clsx(readonly ? styles.containerReadonly : styles.containerEditable)}>
      <Heading
        as={Component}
        id={id}
        className={clsx(
          styles.SectionHeaderTitle,
          readonly ? styles.headingReadonly : styles.headingEditable,
          headingClassName,
        )}
      >
        {title}
      </Heading>

      {!readonly && (
        <Button
          ref={ref}
          {...buttonProps}
          variant="invisible"
          size="small"
          trailingAction={() => (errorPresent ? <AlertIcon className="fgColor-danger" /> : <GearIcon />)}
          block
          className={styles.editButton}
          aria-describedby={errorPresent ? `error-message-${id}` : undefined}
        >
          <span className={clsx('sr-only', styles.screenReaderText)}>{LABELS.editFieldValueLabel(title)}</span>
          {errorPresent && (
            <span id={`error-message-${id}`} className="sr-only">
              {LABELS.errorUpdatingIssueField}
            </span>
          )}
        </Button>
      )}
    </div>
  )

  // Wrap in tooltip if error is present, otherwise return as is
  return errorPresent && !readonly ? <Tooltip text={LABELS.errorUpdatingIssueField}>{content}</Tooltip> : content
}
SectionHeader.displayName = 'SectionHeader'
